import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import fileDownload from 'js-file-download';
import { DateTime } from 'luxon';
import { reset } from '../../stores/ImportStore';
import useSnackBar from '../../hooks/useSnackBar/useSnackBar';
import useDate from '../../hooks/useDate/useDate';
import { UploadFileNames, UploadType, FileFormat } from '../../models/ImportModels';
import ImportService from '../../services/Import/ImportService';
import PageTitle from '../../components/Text/PageTitle';
import Button from '../../components/Button/Button';
import DownloadLinks from './components/DownloadLinks';

function Import() {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const theme = useTheme();
  const snackBar = useSnackBar();
  const { localeDateTimeFormat } = useDate();
  const navigate = useNavigate();

  /**
   * Navigate to import page
   */
  const handleNavigation = () => {
    navigate('/import/file');
  };
  /**
   * Fetches a template file
   * @param type
   */
  const fetchTemplateFile = (type: UploadType, format: FileFormat) => {
    ImportService.api.fetchFile(UploadFileNames[type], format).then(
      (res) => {
        fileDownload(res.data, `${UploadFileNames[type]}_${DateTime.now().toFormat(localeDateTimeFormat)}.${format}`);
      },
      () => {
        snackBar.showSnackBar(t('pages.import.downloadError'), 'error');
      },
    );
  };

  useEffect(() => {
    dispatch(reset());
  }, []);

  return (
    <Grid container sx={{ paddingBottom: '2rem' }}>
      <Grid item xl={6.5} md={6.5} xs={12}>
        <PageTitle>{t('pages.import.title')}</PageTitle>
        <Box
          sx={{
            backgroundColor: theme.palette.monochrome.monoChrome2,
            margin: '1.5rem 0',
            padding: '0 1.5rem 1rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            borderRadius: '0.5rem',
          }}
        >
          <Typography fontSize="28px" variant="body1" paddingTop="1.5rem" lineHeight="normal">
            {t('pages.import.downloadTemplates')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize="20px" fontWeight="700" variant="body1" paddingBottom="0.25rem">
                {t('pages.import.usersTemplate')}
              </Typography>
              <Typography fontSize="18px" fontWeight="400" variant="body2">
                {t('pages.import.usersTemplateDescription')}
              </Typography>
            </Box>
            <DownloadLinks
              downloadExcel={() => {
                fetchTemplateFile(UploadType.USERS, 'xlsx');
              }}
              downloadCSV={() => {
                fetchTemplateFile(UploadType.USERS, 'csv');
              }}
            />
          </Box>
          <Divider sx={{ borderColor: theme.palette.monochrome.monoChrome3, my: 2, margin: 0 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize="20px" fontWeight="700" variant="body1" paddingBottom="0.25rem">
                {t('pages.import.devicesTemplate')}
              </Typography>
              <Typography fontSize="18px" fontWeight="400" variant="body2">
                {t('pages.import.devicesTemplateDescription')}
              </Typography>
            </Box>
            <DownloadLinks
              downloadExcel={() => {
                fetchTemplateFile(UploadType.DEVICES, 'xlsx');
              }}
              downloadCSV={() => {
                fetchTemplateFile(UploadType.DEVICES, 'csv');
              }}
            />
          </Box>
          <Divider sx={{ borderColor: theme.palette.monochrome.monoChrome3, my: 2, margin: 0 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize="20px" fontWeight="700" variant="body1" paddingBottom="0.25rem">
                {t('pages.import.newConnectionsTemplate')}
              </Typography>
              <Typography fontSize="18px" fontWeight="400" variant="body2">
                {t('pages.import.newConnectionsTemplateDescription')}
              </Typography>
            </Box>
            <DownloadLinks
              downloadExcel={() => {
                fetchTemplateFile(UploadType.NEW_CONNECTIONS, 'xlsx');
              }}
              downloadCSV={() => {
                fetchTemplateFile(UploadType.NEW_CONNECTIONS, 'csv');
              }}
            />
          </Box>
          <Divider sx={{ borderColor: theme.palette.monochrome.monoChrome3, my: 2, margin: 0 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize="20px" fontWeight="700" variant="body1" paddingBottom="0.25rem">
                {t('pages.import.simSwapsTemplate')}
              </Typography>
              <Typography fontSize="18px" fontWeight="400" variant="body2">
                {t('pages.import.simSwapsTemplateDescription')}
              </Typography>
            </Box>
            <DownloadLinks
              downloadExcel={() => {
                fetchTemplateFile(UploadType.SIM_SWAPS, 'xlsx');
              }}
              downloadCSV={() => {
                fetchTemplateFile(UploadType.SIM_SWAPS, 'csv');
              }}
            />
          </Box>
        </Box>

        <Typography fontSize="28px" variant="body1" paddingTop="1rem" lineHeight="normal">
          {t('pages.import.importData')}
        </Typography>
        <Typography fontSize="20px" variant="body2" paddingTop="0.25rem" lineHeight="normal">
          {t('pages.import.importDataDescription')}
        </Typography>
        <Button
          color="primaryButton"
          sx={{ marginTop: '1rem', marginBottom: '6rem' }}
          label={t('pages.import.beginImport')}
          onClick={() => {
            handleNavigation();
          }}
        />
      </Grid>
    </Grid>
  );
}

export default Import;
